import classNames from "classnames";
import React, { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { emailFrameDocument } from "./mailbox.utils";
import { PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

export type EmailPreviewData = {
  from?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  html: string;
};

/** Widths of the two views: a computer email program and a phone. */
export const PREVIEW_WIDTHS = { computer: 760, phone: 390 } as const;

/**
 * "Preview" of a Help desk email exactly as it will be sent (owner 2026-10-29: "There's got to be a preview
 * button so I can see how it looks"): From / To / Subject, then the email itself in a sandboxed frame (no
 * scripts), with a Computer / Phone switch. The HTML comes from the server's own Send code.
 */
export const EmailPreviewModal: FC<{
  preview: EmailPreviewData;
  onClose: () => void;
  /** The main Send button of the page, so the owner can send straight from the preview. */
  sendLabel?: string;
  onSend?: () => void;
  sendDisabled?: boolean;
  note?: string;
}> = ({ preview, onClose, sendLabel, onSend, sendDisabled, note }) => {
  const [view, setView] = useState<keyof typeof PREVIEW_WIDTHS>("computer");
  const [height, setHeight] = useState(480);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Grow the frame to the email's own height (re-measured when the view changes).
  const measure = () => {
    const body = frame.current?.contentDocument?.body;
    if (body) setHeight(Math.max(200, body.scrollHeight + 8));
  };
  useEffect(() => {
    const t = window.setTimeout(measure, 60);
    return () => window.clearTimeout(t);
  }, [view, preview.html]);

  return createPortal(
    <div
      className={
        "fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto bg-[#1F2433]/40 px-3 py-8"
      }
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      data-testid={"email-preview"}
    >
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Email preview"}
        className={
          "w-full max-w-[840px] rounded-[16px] bg-white shadow-[0_20px_60px_rgba(31,36,51,0.25)]"
        }
      >
        <div
          className={
            "flex items-center justify-between gap-3 border-b border-[#EDEFF3] px-6 py-4"
          }
        >
          <div className={"text-[17px] font-extrabold text-[#1F2433]"}>
            Preview
          </div>
          <div className={"flex items-center gap-2"}>
            <div
              className={
                "flex rounded-[9px] border border-[#DCE2EA] p-0.5 text-[12.5px] font-bold"
              }
            >
              {(["computer", "phone"] as const).map((v) => (
                <button
                  key={v}
                  type={"button"}
                  onClick={() => setView(v)}
                  aria-pressed={view === v}
                  className={classNames(
                    "rounded-[7px] px-3 py-1",
                    view === v
                      ? "bg-[#6B4EFF] text-white"
                      : "text-[#5D6475] hover:text-[#1F2433]",
                  )}
                >
                  {v === "computer" ? "Computer" : "Phone"}
                </button>
              ))}
            </div>
            <button
              type={"button"}
              onClick={onClose}
              aria-label={"Close"}
              className={
                "flex h-8 w-8 items-center justify-center rounded-full text-[20px] leading-none text-[#5D6475] hover:bg-[#F4F5F8]"
              }
            >
              ×
            </button>
          </div>
        </div>

        <div
          className={
            "space-y-1 border-b border-[#EDEFF3] px-6 py-3 text-[13px] text-[#5D6475]"
          }
        >
          {preview.from && (
            <div>
              <b className={"text-[#1F2433]"}>From:</b> {preview.from}
            </div>
          )}
          {preview.to && (
            <div>
              <b className={"text-[#1F2433]"}>To:</b> {preview.to}
            </div>
          )}
          {preview.cc && (
            <div>
              <b className={"text-[#1F2433]"}>Cc:</b> {preview.cc}
            </div>
          )}
          {preview.bcc && (
            <div>
              <b className={"text-[#1F2433]"}>Bcc:</b> {preview.bcc}{" "}
              <span className={"text-[#8A90A2]"}>
                (the others don&apos;t see this)
              </span>
            </div>
          )}
          {preview.subject && (
            <div>
              <b className={"text-[#1F2433]"}>Subject:</b> {preview.subject}
            </div>
          )}
        </div>

        <div className={"flex justify-center bg-[#F4F5F8] px-3 py-5"}>
          <iframe
            ref={frame}
            title={"Email preview"}
            sandbox={
              "allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            }
            srcDoc={emailFrameDocument(preview.html)}
            onLoad={measure}
            style={{ width: PREVIEW_WIDTHS[view], height, maxWidth: "100%" }}
            className={"rounded-[10px] border border-[#DCE2EA] bg-white"}
          />
        </div>

        <div
          className={
            "flex flex-wrap items-center justify-between gap-3 px-6 py-4"
          }
        >
          <div className={"text-[12.5px] text-[#5D6475]"}>
            {note ||
              "This is exactly what the email will look like. Nothing has been sent."}
          </div>
          <div className={"flex gap-2"}>
            <button
              type={"button"}
              onClick={onClose}
              className={classNames(SMALL_BUTTON, "h-10")}
            >
              Back to editing
            </button>
            {onSend && (
              <button
                type={"button"}
                disabled={sendDisabled}
                onClick={onSend}
                className={classNames(PRIMARY_BUTTON, "h-10 px-5 text-[14px]")}
              >
                {sendLabel || "Send"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
